import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppState } from 'react-native';
import PropTypes from 'prop-types';
import { useStore } from 'Store';
import { getNotificationText } from 'Utils/notificationUtils';
import NotificationDialog from 'LocalGluons/components/NotificationDialog/NotificationDialog';
import { t } from 'Locale/index';
import { Device } from 'DeviceUtils';
import { ConfirmationModal, useConfirmationModal } from 'LocalGluons/components/ConfirmationModal';
import { logEvent } from 'AppManager/index';
import {
    getParentalRatingByModel,
    isContentBlocked,
    isContentLocked
} from 'Utils/parentalControlUtils';
import NavigationService from '../../../runtime/Navigation/navigationService';
import NotificationsService from '../../../NotificationsService';
import { useAppErrorHandler } from '../../../runtime/App/AppErrorHandlerContext/AppErrorHandlerContext';

const notificationOptions = [
    { getLabel: () => t('REMINDER.FIRST_OPTION'), value: 0 },
    { getLabel: () => t('REMINDER.SECOND_OPTION'), value: 5 },
    { getLabel: () => t('REMINDER.THIRD_OPTION'), value: 15 }
];

let timeouts = [];

const clearTimeouts = () => {
    if (timeouts.length > 0) {
        timeouts.map((timeout) => clearTimeout(timeout));
        timeouts = [];
    }
};

export const NotificationsContextProvider = ({ children }) => {
    const { playerData, setPlayerData, appManager, parentalLockConfig } = useStore();
    const { setError } = useAppErrorHandler();
    const { setModalContent, setProtectedContent } = useConfirmationModal();
    const appNotificationsListener = useRef(() => {});
    const systemNotificationsListener = useRef(() => {});
    // Used for reference in notification service
    const currentPlayerData = useRef(playerData);
    const [notifications, setNotifications] = useState({ ...NotificationsService.schedules });

    const updateItem = (itemId) =>
        appManager.getApi().fetchProgramDetail({
            scheduleId: itemId
        });

    const navigateToDetails = useCallback(
        (item, isPlayerOpened) => {
            const navigation = NavigationService.getAppNavigationInstance();
            const navigateParams = { name: 'Details', params: { item }, key: item.getId() };
            const parentalRating = getParentalRatingByModel(item);
            const adultLock = isContentLocked(item.isAdult(), parentalLockConfig);
            const ageBlock = isContentBlocked(parentalRating, parentalLockConfig);
            console.log('navigateParams v navigateToDetails ', adultnavigateParamsLock);
            if (adultLock || ageBlock) {
                if (isPlayerOpened) {
                    // have to wait until notification modal dismiss and then show Pin modal
                    setTimeout(() => {
                        setProtectedContent(
                            item,
                            () => {
                                item.forceUsingChannelId = true;
                                setPlayerData({ item });
                            },
                            true
                        );
                    }, 500);
                } else {
                    setProtectedContent(item, () =>
                        navigation.navigate('rootStack', { screen: 'Details', params: { item } })
                    );
                }
            } else if (isPlayerOpened) {
                item.forceUsingChannelId = true;
                setPlayerData({ item });
            } else {
                navigation.navigate('rootStack', { screen: 'Details', params: { item } });
            }
        },
        [parentalLockConfig]
    );

    const closeModalCallback = () => {
        setModalContent(null);
    };

    useEffect(() => {
        let timeOut;
        const onAppStateChanged = (nextState) => {
            if (nextState !== 'active') {
                clearTimeouts();
            } else {
                // wait for notification handling first
                timeOut = setTimeout(() => {
                    setNotifications({ ...NotificationsService.schedules });
                }, 500);
            }
        };

        const changeListener = AppState.addEventListener('change', onAppStateChanged);
        return () => {
            changeListener.remove();
            if (timeOut?.remove) timeOut.remove();
        };
    }, []);

    const onAppNotificationReceived = async (item, timeOffsetMin) => {
        const isPlayerOpened = Boolean(currentPlayerData.current);
        const text = getNotificationText(timeOffsetMin);
        const itemId = `${item.getId()}`;
        delete NotificationsService.schedules[itemId];
        if (Device.isWeb || AppState.currentState === 'active') {
            setModalContent(
                <NotificationDialog
                    text={`${item.getTitle()} ${text} ${t('NOTIFICATION.SWITCH_QUESTION')}`}
                    isLandscape={isPlayerOpened}
                    onWatchPress={() => navigateToDetails(item, isPlayerOpened)}
                    close={closeModalCallback}
                />
            );
        }
        setNotifications({ ...NotificationsService.schedules });
    };

    const getNotificationByItem = async (item) => NotificationsService.getNotification(item);

    const cancelNotificationByItem = async (item) => {
        logEvent({ type: 'action', subtype: 'reminderDelete', content1: item?.getId() });
        await NotificationsService.cancelScheduledNotification(item);
        setNotifications({ ...NotificationsService.schedules });
    };

    const scheduleNotification = async (item, time) => {
        logEvent({
            type: 'action',
            subtype: 'reminderSet',
            content1: item?.getId(),
            content2: time
        });
        const notificationsEnabled = await NotificationsService.scheduleNotification(item, time);
        if (!notificationsEnabled) {
            const closeModal = () => {
                setModalContent(null);
            };
            setModalContent(
                <ConfirmationModal
                    header={t('COMMON.INFORMATION_MSG')}
                    icon="warning_yellow_black"
                    message={t('NOTIFICATION.SYSTEM_BLOCKED')}
                    confirmLabel={t('COMMON.I_UNDERSTAND')}
                    onConfirm={closeModal}
                    onClose={closeModal}
                />
            );
            return false;
        }
        setNotifications({ ...NotificationsService.schedules });
        return true;
    };

    useEffect(() => {
        currentPlayerData.current = playerData;
    }, [playerData]);

    useEffect(() => {
        const getInitialNotification = async () => {
            await NotificationsService.setup();
            setNotifications({ ...NotificationsService.schedules });
            NotificationsService.getInitialNotification((notification) => {
                if (notification) {
                    updateItem(notification.data.itemId)
                        .then((item) => {
                            navigateToDetails(item);
                        })
                        .catch((error) => {
                            setError({ error, screenId: 'notification' });
                        });
                }
            });
        };
        systemNotificationsListener.current = NotificationsService.subscribeSystemScope(
            ({ data: { itemId } }) => {
                updateItem(itemId)
                    .then((item) => {
                        delete NotificationsService.schedules[itemId];
                        navigateToDetails(item);
                        setNotifications(NotificationsService.schedules);
                    })
                    .catch((error) => {
                        setError({ error, screenId: 'notification' });
                    });
            }
        );
        appNotificationsListener.current = NotificationsService.subscribeAppScope(
            ({ data: { itemId, timeOffsetMin } }) => {
                updateItem(itemId)
                    .then((item) => {
                        onAppNotificationReceived(item, timeOffsetMin);
                    })
                    .catch((error) => {
                        setError({ error, screenId: 'notification' });
                    });
            }
        );
        getInitialNotification();

        return () => {
            // Unsubscribe listeners
            appNotificationsListener.current();
            if (systemNotificationsListener?.current?.remove) {
                systemNotificationsListener.current.remove();
            } else {
                systemNotificationsListener.current();
            }
        };
    }, [parentalLockConfig]);

    useEffect(() => {
        const setNotification = (itemId, notification) => {
            const timer = notification.scheduleTime - Date.now();
            const timeout = setTimeout(() => {
                updateItem(itemId)
                    .then((item) => {
                        onAppNotificationReceived(item, notification.timeOffsetMin);
                    })
                    .catch((error) => {
                        setError({ error, screenId: 'notification' });
                    });
            }, timer);
            timeouts.push(timeout);
        };

        clearTimeouts();
        Object.entries(notifications).forEach(([id, notification]) =>
            setNotification(id, notification)
        );

        return () => {
            clearTimeouts();
        };
    }, [notifications]);

    return (
        <NotificationsContext.Provider
            value={{
                notificationOptions,
                notifications,
                scheduleNotification,
                getNotificationByItem,
                cancelNotificationByItem
            }}
        >
            {children}
        </NotificationsContext.Provider>
    );
};

NotificationsContextProvider.defaultProps = {
    children: []
};

NotificationsContextProvider.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node])
};

export const NotificationsContext = createContext({});

export const useNotifications = () => useContext(NotificationsContext);

export const withNotifications = (Component) => (props) => (
    <NotificationsContext.Consumer>
        {(context) => <Component {...props} {...context} />}
    </NotificationsContext.Consumer>
);
